import { useEffect, useMemo, useState } from 'react'
import { Building2, Coffee } from 'lucide-react'
import { format } from 'date-fns'
import { Button, ConfirmDialog } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  useClockIn,
  useBreakStart,
  useBreakEnd,
  useClockOut,
  useEndDay,
} from '@/features/attendance/hooks/useAttendance'
import type {
  MyAttendanceTodayCompanyEntry,
  MyAttendanceTodayShiftEntry,
  AttendanceToday,
} from '@/features/attendance/types/attendance.types'
import type {
  ShiftAssignment,
  ShiftAssignmentShiftRef,
} from '@/types/shift.types'
import {
  getAssignmentDateLabel,
  getAssignmentLocalDate,
} from '@/features/attendance/utils/shiftDate'
import { hasAttendanceEligibleAssignment } from '@/features/attendance/utils/attendanceEligibility'
import { IncompleteShiftConfirmModal } from '@/features/shifts/components/IncompleteShiftConfirmModal'
import { getIncompleteShiftIssues } from '@/features/shifts/utils/incompleteShift'
import { getLiveElapsedSeconds } from '@/features/attendance/utils/liveElapsed'

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function formatDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0m'
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function formatTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return format(new Date(iso), 'HH:mm')
  } catch {
    return '—'
  }
}

function getShiftLabel(assignment: ShiftAssignment | null): string {
  if (!assignment) return 'Unscheduled'
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? assignment.shiftId
      : null
  return (shift as { name?: string } | null)?.name ?? 'Shift'
}

function getShiftWindow(assignment: ShiftAssignment | null): string | null {
  if (!assignment) return null
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? (assignment.shiftId as ShiftAssignmentShiftRef)
      : null
  const start = assignment.startTime ?? shift?.startTime
  const end = assignment.endTime ?? shift?.endTime
  if (!start || !end) return null
  return `${start}–${end}`
}

function parseHHmmToMs(date: Date, hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  const dt = new Date(date)
  dt.setHours(h, m, 0, 0)
  return dt.getTime()
}

interface ClockInGate {
  kind: 'before' | 'after'
  message: string
}

/**
 * Client-side prediction of whether clock-in is allowed for the shift window.
 * Returns null when no gate fires (within window). The BE is authoritative —
 * this only provides up-front UX feedback instead of a failed click.
 *
 * Cross-midnight shifts (endTime <= startTime) place the end on the next
 * calendar day. Use the assignment date, not the current date, so tomorrow's
 * shift never looks like it is available today.
 */
function computeClockInGate(
  assignment: ShiftAssignment | null,
  nowMs: number,
): ClockInGate | null {
  if (!assignment) return null
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? (assignment.shiftId as ShiftAssignmentShiftRef)
      : null
  const startStr = assignment.startTime ?? shift?.startTime
  const endStr = assignment.endTime ?? shift?.endTime
  if (!startStr || !endStr) return null

  // Assignment-level values (custom slots) win over the template, as on the BE.
  const maxEarly =
    assignment.maxEarlyClockInMinutes ?? shift?.maxEarlyClockInMinutes ?? 10
  const allowPastEnd =
    assignment.allowClockInPastEnd ?? shift?.allowClockInPastEnd ?? false
  const lateCutoff =
    assignment.lateClockInCutoffMinutes ?? shift?.lateClockInCutoffMinutes ?? 0
  const crossesMidnight = endStr <= startStr

  const assignmentDate = getAssignmentLocalDate(assignment) ?? new Date(nowMs)
  const startMs = parseHHmmToMs(assignmentDate, startStr)
  let endMs = parseHHmmToMs(assignmentDate, endStr)
  if (crossesMidnight) endMs += 24 * 60 * 60 * 1000
  const earliestAllowedMs = startMs - maxEarly * 60_000
  const latestAllowedMs = endMs + lateCutoff * 60_000

  if (nowMs < earliestAllowedMs) {
    const minsUntil = Math.ceil((earliestAllowedMs - nowMs) / 60_000)
    const dateLabel = getAssignmentDateLabel(assignment)
    const startsAt = dateLabel ? `on ${dateLabel} at ${startStr}` : `at ${startStr}`
    return {
      kind: 'before',
      message:
        minsUntil > 0
          ? `Shift starts ${startsAt}. Available in ${formatDuration(minsUntil)}.`
          : `Shift starts ${startsAt}.`,
    }
  }
  if (!allowPastEnd && nowMs > latestAllowedMs) {
    return {
      kind: 'after',
      message: `Shift ended at ${endStr}. Request a time correction.`,
    }
  }
  return null
}

function hasShiftWindowEnded(
  assignment: ShiftAssignment | null,
  nowMs: number,
): boolean {
  if (!assignment) return false
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? (assignment.shiftId as ShiftAssignmentShiftRef)
      : null
  const startStr = assignment.startTime ?? shift?.startTime
  const endStr = assignment.endTime ?? shift?.endTime
  if (!startStr || !endStr) return false

  const assignmentDate = getAssignmentLocalDate(assignment) ?? new Date()
  let endMs = parseHHmmToMs(assignmentDate, endStr)
  if (endStr <= startStr) endMs += 24 * 60 * 60 * 1000
  const cutoffMinutes =
    assignment.lateClockInCutoffMinutes ??
    shift?.lateClockInCutoffMinutes ??
    0
  return nowMs >= endMs + cutoffMinutes * 60_000
}

export interface AttendanceCompanyRowProps {
  entry: MyAttendanceTodayCompanyEntry
  companyName: string
  canCreate: boolean
  serverTime?: string
}

export function AttendanceCompanyRow({
  entry,
  companyName,
  canCreate,
  serverTime,
}: AttendanceCompanyRowProps): React.ReactElement {
  const { companyId, shifts } = entry

  if (shifts.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.98))] p-3.5 dark:border-gray-700 dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.68),rgba(31,41,55,0.92))]">
        <Building2
          className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {companyName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No shift scheduled today.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.98))] p-3.5 transition-transform duration-150 hover:-translate-y-[1px] dark:border-gray-700 dark:bg-[linear-gradient(180deg,rgba(31,41,55,0.68),rgba(31,41,55,0.92))]">
      <div className="mb-3 flex items-center gap-2">
        <Building2
          className="h-4 w-4 text-gray-500 dark:text-gray-400"
          aria-hidden
        />
        <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
          {companyName}
        </span>
        {shifts.length > 1 && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            · {shifts.length} shifts
          </span>
        )}
      </div>
      <div className="space-y-2">
        {shifts.map((shift) => (
          <AttendanceShiftRow
            key={shift.shiftAssignmentId}
            shift={shift}
            companyId={companyId}
            companyName={companyName}
            canCreate={canCreate}
            serverTime={serverTime}
          />
        ))}
      </div>
    </div>
  )
}

interface AttendanceShiftRowProps {
  shift: MyAttendanceTodayShiftEntry
  companyId: string
  companyName: string
  canCreate: boolean
  serverTime?: string
}

function AttendanceShiftRow({
  shift,
  companyId,
  companyName,
  canCreate,
  serverTime,
}: AttendanceShiftRowProps): React.ReactElement {
  const { shiftAssignmentId, assignment, attendance } = shift

  const clockInMutation = useClockIn()
  const breakStartMutation = useBreakStart()
  const breakEndMutation = useBreakEnd()
  const clockOutMutation = useClockOut()
  const endDayMutation = useEndDay()

  const [confirmClockOutOpen, setConfirmClockOutOpen] = useState(false)
  const [confirmEndDayOpen, setConfirmEndDayOpen] = useState(false)
  const [incompleteAction, setIncompleteAction] = useState<
    'clock-in' | 'start-break' | 'clock-out' | 'end-day' | null
  >(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const isActive = attendance?.isActive ?? false
  const isOnBreak = attendance?.isOnBreak ?? false
  const dayCompleted = attendance?.dayCompleted === true
  const isRunning = isActive && !isOnBreak

  // Tick while running (live elapsed) and while waiting to clock in, so the
  // clock-in window opens on time instead of only after a reload.
  const needsClock = isRunning || (!isActive && !dayCompleted)
  useEffect(() => {
    if (!needsClock) return
    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [needsClock])

  const displayElapsed = getLiveElapsedSeconds(attendance, nowMs, serverTime)

  const anyPending =
    clockInMutation.isPending ||
    breakStartMutation.isPending ||
    breakEndMutation.isPending ||
    clockOutMutation.isPending ||
    endDayMutation.isPending

  const shiftLabel = useMemo(() => getShiftLabel(assignment), [assignment])
  const shiftWindow = useMemo(() => getShiftWindow(assignment), [assignment])
  const shiftDateLabel = useMemo(
    () => getAssignmentDateLabel(assignment),
    [assignment],
  )
  const incompleteShiftIssues = useMemo(
    () => getIncompleteShiftIssues(assignment),
    [assignment],
  )
  const clockInGate = useMemo(
    () =>
      isActive || !hasAttendanceEligibleAssignment(assignment)
        ? null
        : computeClockInGate(assignment, nowMs),
    [assignment, isActive, nowMs],
  )
  const clockedOutAfterShiftEnd = useMemo(
    () =>
      !isActive &&
      attendance?.clockOut != null &&
      hasShiftWindowEnded(assignment, nowMs),
    [assignment, attendance?.clockOut, isActive, nowMs],
  )
  const shiftEnded = dayCompleted || clockedOutAfterShiftEnd

  const runClockIn = (): void => {
    if (!canCreate || !assignment) return
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    clockInMutation.mutate({
      companyId,
      payload: { shiftAssignmentId, timezone },
    }, {
      onSettled: () => setIncompleteAction(null),
    })
  }

  const runStartBreak = (): void => {
    if (!canCreate) return
    breakStartMutation.mutate({ companyId, payload: {} }, {
      onSettled: () => setIncompleteAction(null),
    })
  }

  const runClockOut = (): void => {
    if (!canCreate) return
    clockOutMutation.mutate(companyId, {
      onSettled: () => {
        setConfirmClockOutOpen(false)
        setIncompleteAction(null)
      },
    })
  }

  const runEndDay = (): void => {
    if (!canCreate) return
    endDayMutation.mutate(companyId, {
      onSettled: () => {
        setConfirmEndDayOpen(false)
        setIncompleteAction(null)
      },
    })
  }

  const requiresIncompleteShiftConfirmation = incompleteShiftIssues.length > 0

  const handleClockIn = (): void => {
    if (requiresIncompleteShiftConfirmation) {
      setIncompleteAction('clock-in')
      return
    }
    runClockIn()
  }

  const handleStartBreak = (): void => {
    if (requiresIncompleteShiftConfirmation) {
      setIncompleteAction('start-break')
      return
    }
    runStartBreak()
  }

  const handleClockOutClick = (): void => {
    if (requiresIncompleteShiftConfirmation) {
      setIncompleteAction('clock-out')
      return
    }
    setConfirmClockOutOpen(true)
  }

  const handleEndDayClick = (): void => {
    if (requiresIncompleteShiftConfirmation) {
      setIncompleteAction('end-day')
      return
    }
    setConfirmEndDayOpen(true)
  }

  const confirmIncompleteShiftAction = (): void => {
    switch (incompleteAction) {
      case 'clock-in':
        runClockIn()
        return
      case 'start-break':
        runStartBreak()
        return
      case 'clock-out':
        runClockOut()
        return
      case 'end-day':
        runEndDay()
        return
      default:
        setIncompleteAction(null)
    }
  }

  const statusToneClass = shiftEnded
    ? 'border-white/80 bg-gradient-to-r from-slate-400/16 to-slate-300/4 dark:border-gray-700 dark:bg-gray-800/80'
    : isRunning
      ? 'border-white/80 bg-gradient-to-r from-emerald-500/16 to-emerald-400/4 dark:border-gray-700 dark:bg-gray-800/80'
      : isOnBreak
        ? 'border-white/80 bg-gradient-to-r from-amber-500/16 to-amber-400/4 dark:border-gray-700 dark:bg-gray-800/80'
        : 'border-white/80 bg-gradient-to-r from-white/90 to-slate-50/80 dark:border-gray-700 dark:bg-gray-800/80'

  const statusDotClass = shiftEnded
    ? 'bg-gray-400'
    : isRunning
      ? 'bg-green-500'
      : isOnBreak
        ? 'bg-amber-500'
        : assignment
          ? 'bg-primary/70'
          : 'bg-gray-300'

  const headlineText = dayCompleted
    ? 'Day complete'
    : clockedOutAfterShiftEnd || clockInGate?.kind === 'after'
      ? 'Shift ended'
    : isRunning
      ? formatElapsed(displayElapsed)
      : isOnBreak
        ? 'On break'
        : clockInGate?.kind === 'before'
          ? 'Not started'
        : assignment
          ? 'Ready to clock in'
          : 'Unscheduled session'

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-2xl border p-3 sm:flex-row sm:items-center sm:justify-between',
        statusToneClass,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={cn(
            'mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full',
            statusDotClass,
            isRunning && 'animate-pulse',
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {shiftLabel}
            </span>
            {shiftWindow && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {shiftWindow}
              </span>
            )}
            {shiftDateLabel && (
              <span className="shrink-0 rounded-full border border-primary/15 bg-primary/5 px-2 py-0.5 text-[11px] font-semibold text-primary dark:border-primary/30 dark:bg-primary/10">
                {shiftDateLabel}
              </span>
            )}
          </div>
          <p className="text-[15px] font-semibold leading-tight text-gray-900 dark:text-gray-100">
            {headlineText}
          </p>
          <div className="space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
            {dayCompleted || clockedOutAfterShiftEnd ? (
              <DayCompletedSummary attendance={attendance!} />
            ) : isActive ? (
              attendance?.clockIn ? (
                <p>Clocked in {formatTime(attendance.clockIn)}</p>
              ) : null
            ) : assignment ? null : (
              <p>Attendance recorded without a scheduled shift.</p>
            )}
          </div>
        </div>
      </div>

      {canCreate && !shiftEnded && (
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-2">
          {!isActive ? (
            clockInGate ? (
              <div className="flex flex-col items-end gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-9 rounded-lg px-4 text-sm"
                  disabled
                >
                  {clockInGate.kind === 'before'
                    ? 'Not started'
                    : 'Shift ended'}
                </Button>
                <span className="max-w-[220px] text-right text-[11px] text-gray-500 dark:text-gray-400">
                  {clockInGate.message}
                </span>
              </div>
            ) : (
              <Button
                type="button"
                variant="primary"
                className="min-h-9 rounded-lg px-4 text-sm"
                onClick={handleClockIn}
                disabled={anyPending || !hasAttendanceEligibleAssignment(assignment)}
              >
                Clock in
              </Button>
            )
          ) : isOnBreak ? (
            <Button
              type="button"
              variant="primary"
              className="min-h-9 rounded-lg px-4 text-sm"
              onClick={() => breakEndMutation.mutate(companyId)}
              disabled={anyPending}
            >
              <Coffee className="mr-1.5 h-4 w-4" aria-hidden />
              End break
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                className="min-h-9 rounded-xl px-3 text-xs"
                onClick={handleStartBreak}
                disabled={anyPending}
              >
                Start break
              </Button>
              <Button
                type="button"
                variant="danger"
                className="min-h-9 rounded-xl px-3 text-xs"
                onClick={handleClockOutClick}
                disabled={anyPending}
              >
                Clock out
              </Button>
              <button
                type="button"
                className="text-xs font-medium text-red-600 underline-offset-2 hover:underline dark:text-red-400 disabled:opacity-50"
                onClick={handleEndDayClick}
                disabled={anyPending}
              >
                End day
              </button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmClockOutOpen}
        onClose={() => setConfirmClockOutOpen(false)}
        onConfirm={runClockOut}
        title="Clock out?"
        message={`Pause ${shiftLabel} at ${companyName}. You can clock back in later today. Worked so far: ${formatElapsed(displayElapsed)}.`}
        confirmLabel="Clock out"
        variant="default"
        confirmLoading={clockOutMutation.isPending}
      />
      <ConfirmDialog
        open={confirmEndDayOpen}
        onClose={() => setConfirmEndDayOpen(false)}
        onConfirm={runEndDay}
        title="End shift?"
        message={`This finalizes ${shiftLabel} at ${companyName}. You won't be able to clock in for this shift again today without manager intervention. Worked: ${formatElapsed(displayElapsed)}.`}
        confirmLabel="End shift"
        variant="danger"
        confirmLoading={endDayMutation.isPending}
      />
      <IncompleteShiftConfirmModal
        open={incompleteAction !== null}
        issues={incompleteShiftIssues}
        onClose={() => setIncompleteAction(null)}
        onConfirm={confirmIncompleteShiftAction}
        confirmLoading={anyPending}
      />
    </div>
  )
}

function DayCompletedSummary({
  attendance,
}: {
  attendance: AttendanceToday
}): React.ReactElement {
  const worked = attendance.totalWorkedMinutes
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
      {attendance.clockIn && <span>In {formatTime(attendance.clockIn)}</span>}
      {attendance.clockOut && (
        <span>Out {formatTime(attendance.clockOut)}</span>
      )}
      <span>Worked {formatElapsed(worked * 60)}</span>
    </div>
  )
}

AttendanceCompanyRow.displayName = 'AttendanceCompanyRow'
